import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { Filter } from "@/shared/lib/filter/filter";
import { Address } from "@/services/backend/addresses/types/address";
import { Contact } from "@/services/backend/contacts/types/contact";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { downloadResponseHandler } from "@/services/backend/_lib/pdf-helpers";
import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { backendApi } from "@/services/backend/api";
import { Confirmation } from "@/services/backend/vbs/confirmations/types/confirmation";
import { ConfirmationBasePatchRequest } from "@/services/backend/documents/types/confirmation";
import { updateCache } from "@/services/backend/_lib/update-cache";
import { DocumentState } from "@/services/backend/documents/types/state";

export const vbsConfirmationApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    vbsConfirmationCreate: build.mutation<Confirmation, CreateRequest>({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "VbsConfirmation", id: "LIST" },
        "VbsConfirmation",
      ],
    }),
    vbsConfirmationCreateFromOld: build.mutation<
      Confirmation,
      CreateFromOldRequest
    >({
      query: (request) => ({
        url: "/vbs/confirmation/create-from-old",
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "VbsConfirmation", id: "LIST" },
        "VbsConfirmation",
      ],
    }),
    vbsConfirmationCreateFromOffer: build.mutation<
      Confirmation,
      CreateFromOfferRequest
    >({
      query: (request) => ({
        url: "/vbs/confirmation/create-from-offer",
        method: "POST",
        body: request,
      }),
      invalidatesTags: () => [
        { type: "VbsConfirmation", id: "LIST" },
        "VbsConfirmation",
      ],
    }),
    vbsConfirmationResetReferenceNumber: build.mutation<
      Confirmation,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/vbs/confirmation/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsConfirmation", id: request.id },
        { type: "VbsConfirmation", id: "LIST" },
      ],
    }),
    vbsConfirmationShow: build.query<Confirmation, ShowRequest>({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "VbsConfirmation", id: result.id }] : [],
    }),
    vbsConfirmationList: build.query<Lst<Confirmation>, Partial<ListRequest>>({
      query: (request) => ({
        url: `/vbs/confirmation/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "VbsConfirmation"),
    }),
    vbsConfirmationPatch: build.mutation<Confirmation, PatchRequest>({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsConfirmation", id: request.id },
        "VbsConfirmation",
        "VbsCalculation",
        "VbsTreatment",
        "VbsPricing",
        "VbsPricingByArea",
      ],
    }),
    vbsConfirmationPdfPreview: build.query<{ objectURL: string }, PDFRequest>({
      ...revokeObjectURLOptions(),
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: objectURLResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    vbsConfirmationPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // Keep the PDF out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    vbsConfirmationUpdateState: build.mutation<
      Confirmation,
      UpdateStateRequest
    >({
      query: (request) => ({
        url: `/vbs/confirmation/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsConfirmation", id: request.id },
        "VbsConfirmation",
      ],
    }),
    vbsConfirmationDelete: build.mutation<Confirmation, DeleteRequest>({
      query: (request) => ({
        url: `/vbs/confirmation`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsConfirmation", id: request.id },
        "VbsConfirmation",
      ],
    }),
  }),
});

export const {
  useVbsConfirmationResetReferenceNumberMutation,
  useVbsConfirmationShowQuery,
  useVbsConfirmationCreateMutation,
  useVbsConfirmationCreateFromOfferMutation,
  useVbsConfirmationCreateFromOldMutation,
  useVbsConfirmationListQuery,
  useVbsConfirmationPatchMutation,
  useVbsConfirmationUpdateStateMutation,
  useVbsConfirmationPdfPreviewQuery,
  useLazyVbsConfirmationPdfPreviewQuery,
  useVbsConfirmationPdfDownloadMutation,
  useVbsConfirmationDeleteMutation,
} = vbsConfirmationApi;

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOfferRequest {
  offerId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOldRequest {
  oldConfirmationId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface DeleteRequest {
  id: string;
}

export type PatchRequest = {
  id: string;
  referenceNumber?: string;
  internalComment?: string;
  serviceAddress?: Address | null;
  serviceContact?: Contact | null;
} & ConfirmationBasePatchRequest;

export interface PDFRequest {
  id: string;
  document: ConfirmationDocument;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export enum ConfirmationDocument {
  All = "all",
  Letter = "letter",
  Treatment = "treatment",
  TreatmentByArea = "treatment-by-area",
}

/**
 * Patches the cached confirmation for an optimistic update. Defined here, at module
 * scope, so it keeps one identity for the whole app — see {@link updateCache}.
 */
export const updateVbsConfirmationCache = updateCache(
  vbsConfirmationApi,
  "vbsConfirmationShow",
);
